import { useRef, useState, useCallback, useEffect } from 'react';
import {
  callGetVoiceToken,
  callProcessExtraction,
  callRecordVoiceUsage,
  callSynthesizeSpeech,
} from '../services/functions';
import { abandonSession, addMessage, getMessages, getSession, updateSession } from '../services/sessions';
import type { SessionType, RealTimeExtraction, RealTimeExtractionType, VoiceProvider } from '../types';

// ─── Types ────────────────────────────────────────────────────────────────────

export type VoiceStatus =
  | 'idle'
  | 'connecting'   // fetching token + opening WebSocket
  | 'active'       // session live
  | 'ending'       // user ended, saving + extracting
  | 'completed'    // extraction done, show summary
  | 'error';

export interface TranscriptEntry {
  role: 'user' | 'assistant';
  text: string;
  timestamp: number;
  isStreaming?: boolean;
  streamKey?: string;
}

export interface VoiceExtractionSummary {
  nodesCreated: number;
  nodesReinforced: number;
  edgesCreated: number;
  motifsIdentified: number;
  contradictionsFound: number;
  updatedNodeCount: number;
  updatedEdgeCount: number;
}

export interface UseVoiceSessionReturn {
  sessionId: string | null;
  status: VoiceStatus;
  transcript: TranscriptEntry[];
  realtimeExtractions: RealTimeExtraction[];
  extractionSummary: VoiceExtractionSummary | null;
  isMuted: boolean;
  isAssistantSpeaking: boolean;
  error: string | null;
  start: (sessionType: SessionType) => Promise<void>;
  resume: (sessionType: SessionType, existingSessionId: string) => Promise<void>;
  end: () => Promise<void>;
  discard: () => Promise<void>;
  disconnect: () => void;
  sendText: (text: string) => void;
  toggleMute: () => void;
  resetEndDetection: () => void;
}

interface UseVoiceSessionOptions {
  onEndIntent?: (utterance: string) => void;
  onAutoEnd?: () => void;
}

interface VoiceUsageTotals {
  inputTextTokens: number;
  outputTextTokens: number;
  inputAudioTokens: number;
  outputAudioTokens: number;
  transcriptionAudioInputTokens: number;
  transcriptionModel?: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const OPENAI_WS_URL = 'wss://api.openai.com/v1/realtime';
const GEMINI_WS_URL =
  'wss://generativelanguage.googleapis.com/ws/google.ai.generativelanguage.v1alpha.GenerativeService.BidiGenerateContentConstrained';
const GROK_WS_URL = 'wss://api.x.ai/v1/realtime';
const SEND_SAMPLE_RATE = 16000;
const RECV_SAMPLE_RATE = 24000;
const FLUSH_SAMPLES = 2048; // ~128 ms at 16 kHz
const FLUSH_INTERVAL_MS = 120;
const TURN_NUDGE_MS = 2800;
const LOCAL_SPEECH_RMS_THRESHOLD = 0.011;
const LOCAL_SPEECH_PEAK_THRESHOLD = 0.07;
const DEFAULT_ECHO_GUARD_MS = 250;
const ELEVENLABS_ECHO_GUARD_MS = 900;
const LOCAL_SPEECH_RECENCY_MS = 5000;
const DUPLICATE_TRANSCRIPT_WINDOW_MS = 8000;
const END_INTENT_COOLDOWN_MS = 4_000;
const WS_OPEN_TIMEOUT_MS = 20_000;
const RESUME_SEED_MESSAGE_LIMIT = 40;
const STREAM_KEY_FALLBACK_USER = 'user-live';
const STREAM_KEY_FALLBACK_ASSISTANT = 'assistant-live';
let geminiTurnCounter = 0;
const GEMINI_STALL_TIMEOUT_MS = 8000;
const GEMINI_STALL_RETRY_MS = 5000;
const GEMINI_STALL_MAX_RETRIES = 2;
function getVoiceKickoffPrompt(sessionType: SessionType): string {
  if (sessionType === 'onboarding') {
    return [
      'Start an onboarding get-to-know-you session.',
      'In your first reply, briefly welcome the user and set a warm expectation that this is a short conversation to get to know them — what matters to them, how they think, and what they want help with.',
      'Then ask your first life-context question — just one — and stop. Let them answer fully before you ask anything else.',
      'Do not stack a second question onto the opener.',
      'Do not use generic daily-check-in phrasing like "what is on your mind today".',
    ].join(' ');
  }

  if (sessionType === 'morning_checkin') {
    return [
      'Start a morning check-in now.',
      'Greet warmly in one short sentence, then ask one open question about how the user is feeling this morning.',
      'Then stop and listen — let the user share before you probe further. Do not stack a second question onto the opener.',
    ].join(' ');
  }

  if (sessionType === 'evening_checkin') {
    return [
      'Start an evening check-in now.',
      'Greet warmly in one short sentence, then ask one open question about how the day went.',
      'Then stop and listen — let the user share before you probe further. Do not stack a second question onto the opener.',
    ].join(' ');
  }

  if (sessionType === 'task_planning') {
    return [
      'Start a task planning session now.',
      'Greet warmly in one short sentence, then ask one open question about what task or goal the user wants to think through.',
      'Then stop and listen — let them describe the task fully before you ask anything else. Do not stack a second question onto the opener.',
      'Do not use generic check-in phrasing.',
    ].join(' ');
  }

  return [
    'Start this ad-hoc coaching conversation now.',
    'Greet warmly in one short sentence, then invite the user to share what they want support with.',
    'Then stop and listen — let them talk before you ask anything else. Do not stack a second question onto the opener.',
  ].join(' ');
}
// Explicit "end the session" language
const END_SESSION_INTENT_PATTERNS = [
  /\b(end|finish|stop|close|wrap(?:\s+up)?)\b[\s\w]{0,30}\b(session|conversation|chat|call|here)\b/i,
  /\bend\s+this\s+conversation\s+here\b/i,
  /\b(i(?:\s*am|'m)?\s+done|that(?:'s| is)\s+all|we(?:'re| are)\s+done|enough\s+for\s+today)\b/i,
  /\blet(?:'s|\s+me)\s+(end|finish|stop|wrap(?:\s+up)?|leave\s+it)\b/i,
  /\b(that(?:'s| is)\s+enough|i(?:'m|\s+am)\s+good|leave\s+it\s+(there|here|at\s+that))\b/i,
  /\b(stop\s+(there|here)|end\s+(it|this)\s+(there|here)|we(?:'ll|\s+will)\s+end\s+(the\s+)?(session|conversation|chat|call))\b/i,
  /\b(done\s+for\s+now|wrap\s+for\s+now)\b/i,
];

// Natural farewell language from the user (goodbye, night night, speak tomorrow, etc.)
const USER_FAREWELL_PATTERNS = [
  /\b(good\s*night|night\s*night|nighty?\s*night|g'?night)\b/i,
  /\b(good\s*bye|bye\s*bye|bye\s+for\s+now|bye)\b/i,
  /\b(take\s+care)\b/i,
  /\b(speak|talk|chat|see\s+you|catch\s+you)\s+(to\s+you\s+)?(tomorrow|later|soon|next\s+time)\b/i,
  /\b(until\s+(tomorrow|next\s+time|later))\b/i,
  /\b(have\s+a\s+(good|great|lovely|wonderful)\s+(night|evening|one|day))\b/i,
  /\b(good\s+evening)\b[\s,.!]*$/i,
  /\b(thanks|thank\s+you)\s+(for\s+(your\s+|the\s+)?help)\b/i,
  /\b(cheers|thanks\s+a\s+(lot|bunch|million))\b[\s,.!]*$/i,
];

const COACH_FAREWELL_PATTERNS = [
  /\b(have\s+a\s+(great|good|wonderful|lovely)\s+(day|evening|morning|night|one|rest))\b/i,
  /\b(take\s+care|until\s+next\s+time|(speak|talk|chat)\s+(again\s+)?soon|catch\s+up\s+(again\s+)?soon)\b/i,
  /\b(go\s+(get|do)\s+(it|them|'em))\b/i,
  /\b(good\s+luck)\b[\s,.!]*$/i,
  /\b(i'?ll\s+leave\s+you\s+to\s+it|off\s+you\s+go)\b/i,
  /\b((you|we)'?re\s+(all\s+)?set)\b[\s,.!]*$/i,
  /\b(we('?ll|\s+can)\s+pick\s+(it|this|up))\b/i,
  /\b(we'?ll\s+(pause|stop|wrap\s+up|leave\s+it)\s+here)\b/i,
  /\b(see\s+you\s+(next\s+time|soon|later|tomorrow))\b/i,
  /\b(bye\s+for\s+now|goodbye|good\s*bye)\b/i,
  /\b(whenever\s+you'?re\s+ready\s+next\s+time)\b/i,
  /\b(rest\s+well|sleep\s+well|rest\s+up)\b/i,
  /\b(good\s*night|night\s*night)\b/i,
  /\b(speak|talk|chat)\s+(to\s+you\s+)?(tomorrow|later|next\s+time)\b/i,
  /\b(good\s+evening)\b[\s,.!]*$/i,
  /\b(until\s+tomorrow)\b/i,
  /\b(whenever\s+you'?re\s+ready)\b/i,
  /\b(feel\s+free\s+to\s+(jump|come)\s+back\s+in)\b/i,
  /\b(jump\s+back\s+in)\b/i,
  /\b(pick\s+this\s+back\s+up\s+(anytime|later)|pick\s+this\s+up\s+(anytime|later))\b/i,
  /\b(we\s+can\s+(pause|stop|wrap\s+up|leave\s+it)\s+here)\b/i,
  /\b(pick\s+it\s+up\s+whenever\s+you'?re\s+ready)\b/i,
  /\b(dive\s+back\s+in)\b/i,
];

function normalizeIntentText(text: string): string {
  return text
    .toLowerCase()
    .replace(/[\u2018\u2019]/g, '\'')
    .replace(/[\u201c\u201d]/g, '"')
    .replace(/[^a-z0-9'\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeTranscriptForDedupe(text: string): string {
  return text
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

function getTimestampMillis(timestamp: { toMillis?: () => number; seconds?: number } | Date): number {
  if (timestamp instanceof Date) return timestamp.getTime();
  if (timestamp?.toMillis) return timestamp.toMillis();
  if (typeof timestamp?.seconds === 'number') return timestamp.seconds * 1000;
  return Date.now();
}

function toUserFacingErrorMessage(error: unknown, fallback: string): string {
  const rawMessage = error instanceof Error
    ? error.message
    : typeof error === 'string'
      ? error
      : fallback;

  // Firebase callable errors are often prefixed like "resource-exhausted: ..."
  // Strip machine prefixes so users see the actionable sentence.
  const cleaned = rawMessage
    .replace(/^firebase:\s*/i, '')
    .replace(/^[a-z-]+:\s*/i, '')
    .trim();

  return cleaned || fallback;
}

function createEmptyVoiceUsageTotals(): VoiceUsageTotals {
  return {
    inputTextTokens: 0,
    outputTextTokens: 0,
    inputAudioTokens: 0,
    outputAudioTokens: 0,
    transcriptionAudioInputTokens: 0,
  };
}

function toTokenCount(value: unknown): number {
  const parsed = Number(value ?? 0);
  if (!Number.isFinite(parsed) || parsed <= 0) return 0;
  return Math.round(parsed);
}

function asObject(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object'
    ? (value as Record<string, unknown>)
    : null;
}

function extractOpenAiUsageDelta(msg: Record<string, unknown>): Partial<VoiceUsageTotals> {
  const response = asObject(msg.response);
  const usage = asObject(response?.usage);
  if (!usage) return {};

  const inputDetails = asObject(usage.input_token_details ?? usage.inputTokenDetails);
  const outputDetails = asObject(usage.output_token_details ?? usage.outputTokenDetails);

  const inputTextTokens = toTokenCount(inputDetails?.text_tokens ?? inputDetails?.textTokens);
  const inputAudioTokens = toTokenCount(inputDetails?.audio_tokens ?? inputDetails?.audioTokens);
  const outputTextTokens = toTokenCount(outputDetails?.text_tokens ?? outputDetails?.textTokens);
  const outputAudioTokens = toTokenCount(outputDetails?.audio_tokens ?? outputDetails?.audioTokens);

  const fallbackInputTokens = toTokenCount(
    usage.input_tokens
    ?? usage.inputTokens
    ?? usage.prompt_tokens
    ?? usage.promptTokenCount
  );
  const fallbackOutputTokens = toTokenCount(
    usage.output_tokens
    ?? usage.outputTokens
    ?? usage.completion_tokens
    ?? usage.candidatesTokenCount
  );

  const transcriptionAudioInputTokens = toTokenCount(
    usage.input_audio_transcription_tokens
    ?? usage.transcription_input_tokens
    ?? usage.transcriptionTokens
    ?? inputDetails?.transcription_tokens
    ?? inputDetails?.transcriptionTokens
  );
  const transcriptionModelRaw = usage.input_audio_transcription_model ?? usage.transcriptionModel;
  const transcriptionModel = typeof transcriptionModelRaw === 'string'
    ? transcriptionModelRaw.trim()
    : undefined;

  return {
    inputTextTokens: inputTextTokens > 0 ? inputTextTokens : fallbackInputTokens,
    outputTextTokens: outputTextTokens > 0 ? outputTextTokens : fallbackOutputTokens,
    inputAudioTokens,
    outputAudioTokens,
    transcriptionAudioInputTokens,
    transcriptionModel: transcriptionModel || undefined,
  };
}

function extractGeminiUsageDelta(msg: Record<string, unknown>): Partial<VoiceUsageTotals> {
  const directUsage = asObject(msg.usageMetadata ?? msg.usage_metadata);
  const serverContent = asObject(msg.serverContent ?? msg.server_content);
  const nestedUsage = asObject(serverContent?.usageMetadata ?? serverContent?.usage_metadata);
  const usage = nestedUsage ?? directUsage;
  if (!usage) return {};

  const readDetails = (raw: unknown): { text: number; audio: number } => {
    if (!Array.isArray(raw)) return { text: 0, audio: 0 };
    let text = 0;
    let audio = 0;
    for (const item of raw) {
      const detail = asObject(item);
      if (!detail) continue;
      const count = toTokenCount(detail.tokenCount ?? detail.token_count);
      const modality = String(detail.modality ?? '').toUpperCase();
      if (!count) continue;
      if (modality === 'AUDIO') audio += count;
      else if (modality === 'TEXT') text += count;
    }
    return { text, audio };
  };

  const prompt = readDetails(usage.promptTokensDetails ?? usage.prompt_tokens_details);
  const candidates = readDetails(usage.candidatesTokensDetails ?? usage.candidates_tokens_details);

  const promptTokenCount = toTokenCount(usage.promptTokenCount ?? usage.prompt_token_count);
  const candidateTokenCount = toTokenCount(usage.candidatesTokenCount ?? usage.candidates_token_count);

  return {
    inputTextTokens: prompt.text > 0 ? prompt.text : promptTokenCount,
    inputAudioTokens: prompt.audio,
    outputTextTokens: candidates.text > 0 ? candidates.text : candidateTokenCount,
    outputAudioTokens: candidates.audio,
  };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useVoiceSession(
  userId: string | null,
  options?: UseVoiceSessionOptions
): UseVoiceSessionReturn {
  // ── State ─────────────────────────────────────────────────────────────────
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [status, setStatus] = useState<VoiceStatus>('idle');
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
  const [realtimeExtractions, setRealtimeExtractions] = useState<RealTimeExtraction[]>([]);
  const [extractionSummary, setExtractionSummary] = useState<VoiceExtractionSummary | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isAssistantSpeaking, setIsAssistantSpeaking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Refs (stable across renders, safe to use in closures) ─────────────────
  const wsRef = useRef<WebSocket | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const captureCtxRef = useRef<AudioContext | null>(null);
  const playbackCtxRef = useRef<AudioContext | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);

  // Audio send buffer
  const sendBufferRef = useRef<Float32Array[]>([]);
  const sendBufferLenRef = useRef(0);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Playback scheduling
  const nextPlayTimeRef = useRef(0);
  const speakingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const turnNudgeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const userAudioSinceLastTurnRef = useRef(false);
  const suppressUserAudioUntilRef = useRef(0);
  const lastLocalSpeechAtRef = useRef(0);

  // Transcript as ref so `end()` always reads the latest without closure issues
  const transcriptRef = useRef<TranscriptEntry[]>([]);
  const persistedTranscriptOffsetRef = useRef(0);

  // Partial transcript text accumulates across chunks until turnComplete
  const inputPartialRef = useRef('');
  const outputPartialRef = useRef('');
  const userStreamingKeyRef = useRef<string | null>(null);
  const assistantStreamingKeyRef = useRef<string | null>(null);
  const transcriptStreamIndexRef = useRef<Map<string, number>>(new Map());
  const completedStreamKeysRef = useRef<Set<string>>(new Set());
  const assistantStreamChannelRef = useRef<Map<string, 'audio' | 'text'>>(new Map());

  // Mute ref for use inside worklet message handler (avoids stale closure)
  const isMutedRef = useRef(false);

  // Speaking ref for use in timer callbacks (avoids stale closure on isAssistantSpeaking state)
  const isAssistantSpeakingRef = useRef(false);

  // Duplicate response detection: prevents looping when the model repeats itself
  const consecutiveDuplicateCountRef = useRef(0);
  const lastAssistantResponseTextRef = useRef('');
  const DUPLICATE_THRESHOLD = 2;

  // Cooldown after a response completes before accepting turn nudges
  const lastResponseDoneAtRef = useRef(0);
  const RESPONSE_COOLDOWN_MS = 4000;

  // Intentional close flag so we don't show error on normal end
  const intentionalCloseRef = useRef(false);
  const startingRef = useRef(false);
  const isUnmountedRef = useRef(false);
  const activeProviderRef = useRef<VoiceProvider>('openai');
  const activeModelRef = useRef<string>('gpt-realtime-2');
  const elevenVoiceIdRef = useRef<string | undefined>(undefined);
  const voiceUsageTotalsRef = useRef<VoiceUsageTotals>(createEmptyVoiceUsageTotals());
  const voiceUsageReportIdRef = useRef<string>('');
  const sessionStartedAtRef = useRef<number>(0);
  const openAiResponseInProgressRef = useRef(false);
  const queuedOpenAiResponseCreateRef = useRef(false);
  const handledToolCallIdsRef = useRef<Set<string>>(new Set());
  const lastUserEndIntentAtRef = useRef(0);
  const lastCoachFarewellAtRef = useRef(0);
  const userEndIntentDetectedRef = useRef(false);
  const autoEndTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const geminiStallTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const geminiStallRetriesRef = useRef(0);
  const geminiLastActivityRef = useRef(0);

  // Keep callbacks in refs so the WebSocket handler always calls the latest version
  const onEndIntentRef = useRef(options?.onEndIntent);
  onEndIntentRef.current = options?.onEndIntent;
  const onAutoEndRef = useRef(options?.onAutoEnd);
  onAutoEndRef.current = options?.onAutoEnd;

  function isLikelyEndIntent(text: string): boolean {
    const normalized = normalizeIntentText(text);
    if (!normalized) return false;
    return END_SESSION_INTENT_PATTERNS.some(p => p.test(normalized))
      || USER_FAREWELL_PATTERNS.some(p => p.test(normalized));
  }

  function isCoachFarewell(text: string): boolean {
    const normalized = normalizeIntentText(text);
    if (!normalized) return false;
    return COACH_FAREWELL_PATTERNS.some(pattern => pattern.test(normalized));
  }

  async function parseWsJson(data: unknown): Promise<Record<string, unknown> | null> {
    let text: string;
    if (typeof data === 'string') {
      text = data;
    } else if (data instanceof Blob) {
      text = await data.text();
    } else if (data instanceof ArrayBuffer) {
      text = new TextDecoder().decode(new Uint8Array(data));
    } else {
      return null;
    }

    try {
      return JSON.parse(text) as Record<string, unknown>;
    } catch {
      console.log('[OpenAI Realtime] non-JSON frame:', text.slice(0, 200));
      return null;
    }
  }

  function logVoiceDebug(label: string, payload?: unknown) {
    if (payload === undefined) {
      console.log(`[voice] ${label}`);
      return;
    }
    console.log(`[voice] ${label}`, payload);
  }

  function logVoiceDebugJson(label: string, payload: unknown) {
    try {
      console.log(`[voice] ${label} ${JSON.stringify(payload)}`);
    } catch {
      console.log(`[voice] ${label}`, payload);
    }
  }

  function resetVoiceUsageTracking() {
    voiceUsageTotalsRef.current = createEmptyVoiceUsageTotals();
    voiceUsageReportIdRef.current = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `usage-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    sessionStartedAtRef.current = Date.now();
  }

  function addVoiceUsageDelta(delta: Partial<VoiceUsageTotals>) {
    const usage = voiceUsageTotalsRef.current;
    const inputText = toTokenCount(delta.inputTextTokens);
    if (inputText > 0) usage.inputTextTokens += inputText;
    const outputText = toTokenCount(delta.outputTextTokens);
    if (outputText > 0) usage.outputTextTokens += outputText;
    const inputAudio = toTokenCount(delta.inputAudioTokens);
    if (inputAudio > 0) usage.inputAudioTokens += inputAudio;
    const outputAudio = toTokenCount(delta.outputAudioTokens);
    if (outputAudio > 0) usage.outputAudioTokens += outputAudio;
    const transcriptionAudio = toTokenCount(delta.transcriptionAudioInputTokens);
    if (transcriptionAudio > 0) usage.transcriptionAudioInputTokens += transcriptionAudio;
    if (
      typeof delta.transcriptionModel === 'string'
      && delta.transcriptionModel.trim().length > 0
    ) {
      usage.transcriptionModel = delta.transcriptionModel.trim();
    }
  }

  function currentVoiceDurationSeconds(): number {
    if (!sessionStartedAtRef.current) return 0;
    return Math.max(0, Math.round((Date.now() - sessionStartedAtRef.current) / 1000));
  }

  const reportVoiceUsageBestEffort = useCallback(async (activeSessionId: string): Promise<void> => {
    const usage = voiceUsageTotalsRef.current;
    const durationSeconds = currentVoiceDurationSeconds();
    const hasUsage =
      usage.inputTextTokens > 0
      || usage.outputTextTokens > 0
      || usage.inputAudioTokens > 0
      || usage.outputAudioTokens > 0
      || usage.transcriptionAudioInputTokens > 0
      || (activeProviderRef.current === 'grok' && durationSeconds > 0);
    if (!hasUsage) return;

    try {
      await callRecordVoiceUsage({
        sessionId: activeSessionId,
        provider: activeProviderRef.current,
        model: activeModelRef.current,
        durationSeconds,
        reportId: voiceUsageReportIdRef.current,
        usage,
      });
    } catch (err) {
      console.warn('Failed to record voice usage billing:', err);
    }
  }, []);

  function requestOpenAiResponse() {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    if (openAiResponseInProgressRef.current) {
      queuedOpenAiResponseCreateRef.current = true;
      logVoiceDebug('response.create queued (response already in progress)');
      return;
    }
    logVoiceDebug('sending response.create');
    ws.send(JSON.stringify({ type: 'response.create' }));
    openAiResponseInProgressRef.current = true;
  }

  function getActiveEchoGuardMs(): number {
    return activeProviderRef.current === 'elevenlabs'
      ? ELEVENLABS_ECHO_GUARD_MS
      : DEFAULT_ECHO_GUARD_MS;
  }

  function shouldApplyEchoGuard(): boolean {
    return activeProviderRef.current === 'elevenlabs';
  }

  // ── Audio helpers ─────────────────────────────────────────────────────────

  function isWithinAssistantEchoGuard(): boolean {
    if (!shouldApplyEchoGuard()) return false;
    return isAssistantSpeakingRef.current || Date.now() < suppressUserAudioUntilRef.current;
  }

  function hasRecentLocalSpeechEnergy(): boolean {
    return Date.now() - lastLocalSpeechAtRef.current <= LOCAL_SPEECH_RECENCY_MS;
  }

  function hasSpeechLikeEnergy(samples: Float32Array): boolean {
    if (samples.length === 0) return false;
    let sumSquares = 0;
    let peak = 0;
    for (let i = 0; i < samples.length; i++) {
      const value = samples[i];
      const abs = Math.abs(value);
      sumSquares += value * value;
      if (abs > peak) peak = abs;
    }
    const rms = Math.sqrt(sumSquares / samples.length);
    return rms >= LOCAL_SPEECH_RMS_THRESHOLD || peak >= LOCAL_SPEECH_PEAK_THRESHOLD;
  }

  function flushSendBuffer() {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || sendBufferLenRef.current === 0) return;

    // Concatenate all buffered Float32 chunks
    const merged = new Float32Array(sendBufferLenRef.current);
    let offset = 0;
    for (const chunk of sendBufferRef.current) {
      merged.set(chunk, offset);
      offset += chunk.length;
    }
    sendBufferRef.current = [];
    sendBufferLenRef.current = 0;
    const speechLikeEnergy = hasSpeechLikeEnergy(merged);

    // Float32 [-1,1] → Int16
    const int16 = new Int16Array(merged.length);
    for (let i = 0; i < merged.length; i++) {
      const s = Math.max(-1, Math.min(1, merged[i]));
      int16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }

    // Base64 encode
    const bytes = new Uint8Array(int16.buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    const b64 = btoa(binary);

    if (activeProviderRef.current === 'openai' || activeProviderRef.current === 'elevenlabs' || activeProviderRef.current === 'grok') {
      ws.send(JSON.stringify({ type: 'input_audio_buffer.append', audio: b64 }));
    } else if (activeProviderRef.current === 'gemini') {
      ws.send(
        JSON.stringify({
          realtimeInput: {
            audio: { data: b64, mimeType: `audio/pcm;rate=${SEND_SAMPLE_RATE}` },
          },
        })
      );
    }
    if (speechLikeEnergy) {
      lastLocalSpeechAtRef.current = Date.now();
      userAudioSinceLastTurnRef.current = true;
      scheduleTurnNudge();
      if (activeProviderRef.current === 'gemini') {
        scheduleGeminiStallRecovery();
      }
    }
  }

  function schedulePendingFlush() {
    if (flushTimerRef.current) return;
    flushTimerRef.current = setTimeout(() => {
      flushTimerRef.current = null;
      flushSendBuffer();
    }, FLUSH_INTERVAL_MS);
  }

  function enqueuePlayback(base64: string, sampleRate: number = RECV_SAMPLE_RATE) {
    const ctx = playbackCtxRef.current;
    if (!ctx) return;

    // Base64 → Int16Array
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const int16 = new Int16Array(bytes.buffer);

    // Int16 → Float32
    const float32 = new Float32Array(int16.length);
    for (let i = 0; i < int16.length; i++) float32[i] = int16[i] / 32768;

    // Create AudioBuffer at 24 kHz (browser resamples to AudioContext rate)
    const buffer = ctx.createBuffer(1, float32.length, sampleRate);
    buffer.copyToChannel(float32, 0);

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);

    const now = ctx.currentTime;
    const startAt = Math.max(now, nextPlayTimeRef.current);
    source.start(startAt);
    nextPlayTimeRef.current = startAt + buffer.duration;
    if (shouldApplyEchoGuard()) {
      const echoGuardMs = getActiveEchoGuardMs();
      suppressUserAudioUntilRef.current = Math.max(
        suppressUserAudioUntilRef.current,
        Date.now() + Math.ceil(buffer.duration * 1000) + echoGuardMs
      );
    }

    setIsAssistantSpeaking(true);
    isAssistantSpeakingRef.current = true;
    if (speakingTimerRef.current) clearTimeout(speakingTimerRef.current);
    speakingTimerRef.current = setTimeout(() => {
      if (nextPlayTimeRef.current <= (playbackCtxRef.current?.currentTime ?? 0) + 0.1) {
        setIsAssistantSpeaking(false);
        isAssistantSpeakingRef.current = false;
      }
    }, (buffer.duration + 0.2) * 1000);
  }

  function handleTranscriptIntentSignals(role: 'user' | 'assistant', text: string, now: number) {
    if (intentionalCloseRef.current) return;

    if (role === 'user' && isLikelyEndIntent(text)) {
      if (now - lastUserEndIntentAtRef.current > END_INTENT_COOLDOWN_MS) {
        lastUserEndIntentAtRef.current = now;
        userEndIntentDetectedRef.current = true;
        onEndIntentRef.current?.(text);
      }
    }

    if (role === 'assistant' && isCoachFarewell(text)) {
      if (now - lastCoachFarewellAtRef.current > END_INTENT_COOLDOWN_MS) {
        lastCoachFarewellAtRef.current = now;
        if (userEndIntentDetectedRef.current) {
          onAutoEndRef.current?.();
        } else {
          onEndIntentRef.current?.(text);
        }
      }
    }
  }

  function streamIndexKey(role: 'user' | 'assistant', streamKey: string): string {
    return `${role}:${streamKey}`;
  }

  function normalizeStreamKey(role: 'user' | 'assistant', streamKey?: string): string {
    if (streamKey?.trim()) return streamKey;
    return role === 'user' ? STREAM_KEY_FALLBACK_USER : STREAM_KEY_FALLBACK_ASSISTANT;
  }

  function mergeTranscriptText(previousText: string, nextText: string): string {
    const previous = previousText.trim();
    const next = nextText.trim();
    if (!previous) return next;
    if (!next) return previous;
    if (previous === '...') return next;

    const previousNorm = normalizeTranscriptForDedupe(previous);
    const nextNorm = normalizeTranscriptForDedupe(next);
    if (previousNorm === nextNorm) return previous;
    if (previousNorm.endsWith(nextNorm)) return previous;
    if (nextNorm.startsWith(previousNorm)) return next;
    return `${previous} ${next}`.replace(/\s+/g, ' ').trim();
  }

  function resolveUserStreamKey(msg?: Record<string, unknown>): string {
    const activeStreamKey = userStreamingKeyRef.current;
    if (activeStreamKey) {
      const activeIndexKey = streamIndexKey('user', activeStreamKey);
      if (transcriptStreamIndexRef.current.has(activeIndexKey)) return activeStreamKey;
    }
    const itemId = msg && typeof msg.item_id === 'string' ? msg.item_id : null;
    const contentIndex = msg && typeof msg.content_index === 'number' ? msg.content_index : null;
    if (itemId) return contentIndex == null ? itemId : `${itemId}:${contentIndex}`;
    if (activeStreamKey) return activeStreamKey;
    return STREAM_KEY_FALLBACK_USER;
  }

  function resolveAssistantStreamKey(msg?: Record<string, unknown>): string {
    if (msg) {
      const responseId = typeof msg.response_id === 'string' ? msg.response_id : null;
      if (responseId) return responseId;
      const response = msg.response as Record<string, unknown> | undefined;
      const nestedResponseId = typeof response?.id === 'string' ? response.id : null;
      if (nestedResponseId) return nestedResponseId;
      const keyed = assistantTurnKey(msg);
      if (keyed) return keyed;
    }
    if (assistantStreamingKeyRef.current) return assistantStreamingKeyRef.current;
    return STREAM_KEY_FALLBACK_ASSISTANT;
  }

  function appendTranscript(
    role: 'user' | 'assistant',
    text: string,
    options?: { suppressIntent?: boolean; isStreaming?: boolean; streamKey?: string }
  ) {
    const now = Date.now();
    const normalizedText = normalizeTranscriptForDedupe(text);
    const lastEntry = transcriptRef.current[transcriptRef.current.length - 1];
    if (lastEntry && !options?.isStreaming) {
      const sameRole = lastEntry.role === role;
      const withinWindow = now - lastEntry.timestamp <= DUPLICATE_TRANSCRIPT_WINDOW_MS;
      const sameText = normalizeTranscriptForDedupe(lastEntry.text) === normalizedText;
      if (sameRole && withinWindow && sameText) return;
    }

    const entry: TranscriptEntry = {
      role,
      text,
      timestamp: now,
      isStreaming: options?.isStreaming ?? false,
      streamKey: options?.streamKey,
    };
    const updated = [...transcriptRef.current, entry];
    transcriptRef.current = updated;
    setTranscript(updated);

    if (entry.streamKey) {
      transcriptStreamIndexRef.current.set(streamIndexKey(role, entry.streamKey), updated.length - 1);
    }

    if (!options?.suppressIntent) {
      handleTranscriptIntentSignals(role, text, now);
    }
  }

  function refreshStreamIndicesFrom(startIndex: number) {
    const streamIndex = transcriptStreamIndexRef.current;
    for (const [key, index] of streamIndex.entries()) {
      if (index > startIndex) {
        streamIndex.set(key, index - 1);
      }
    }
  }

  function removeStreamingTranscript(role: 'user' | 'assistant', streamKey: string) {
    const normalizedKey = normalizeStreamKey(role, streamKey);
    const indexKey = streamIndexKey(role, normalizedKey);
    const entryIndex = transcriptStreamIndexRef.current.get(indexKey);
    if (entryIndex == null) return;

    const updated = transcriptRef.current.filter((_, i) => i !== entryIndex);
    transcriptRef.current = updated;
    setTranscript(updated);
    transcriptStreamIndexRef.current.delete(indexKey);
    refreshStreamIndicesFrom(entryIndex);

    if (role === 'user' && userStreamingKeyRef.current === normalizedKey) userStreamingKeyRef.current = null;
    if (role === 'assistant' && assistantStreamingKeyRef.current === normalizedKey) assistantStreamingKeyRef.current = null;
  }

  function upsertStreamingTranscript(role: 'user' | 'assistant', streamKey: string, text: string) {
    const normalizedKey = normalizeStreamKey(role, streamKey);
    const indexKey = streamIndexKey(role, normalizedKey);
    if (completedStreamKeysRef.current.has(indexKey)) {
      if (role === 'assistant') return;
      completedStreamKeysRef.current.delete(indexKey);
    }

    const nextText = text.trim();
    if (!nextText) return;

    if (role === 'user') userStreamingKeyRef.current = normalizedKey;
    if (role === 'assistant') assistantStreamingKeyRef.current = normalizedKey;

    const entryIndex = transcriptStreamIndexRef.current.get(indexKey);
    if (entryIndex == null) {
      appendTranscript(role, nextText, {
        isStreaming: true,
        streamKey: normalizedKey,
        suppressIntent: true,
      });
      return;
    }

    const existing = transcriptRef.current[entryIndex];
    if (!existing) {
      transcriptStreamIndexRef.current.delete(indexKey);
      appendTranscript(role, nextText, {
        isStreaming: true,
        streamKey: normalizedKey,
        suppressIntent: true,
      });
      return;
    }

    if (existing.text === nextText && existing.isStreaming) return;

    const updated = [...transcriptRef.current];
    updated[entryIndex] = {
      ...existing,
      text: nextText,
      isStreaming: true,
      streamKey: normalizedKey,
      timestamp: Date.now(),
    };
    transcriptRef.current = updated;
    setTranscript(updated);
  }

  function finalizeStreamingTranscript(role: 'user' | 'assistant', streamKey: string, finalText?: string): boolean {
    const normalizedKey = normalizeStreamKey(role, streamKey);
    const indexKey = streamIndexKey(role, normalizedKey);
    const entryIndex = transcriptStreamIndexRef.current.get(indexKey);
    const normalizedFinalText = (finalText ?? '').trim();
    if (
      role === 'user'
      && completedStreamKeysRef.current.has(indexKey)
      && entryIndex == null
      && normalizedFinalText
    ) {
      // Some providers reuse coarse IDs or emit "completed" without prior deltas.
      // Re-open the key for this finalized turn and rely on transcript dedupe.
      completedStreamKeysRef.current.delete(indexKey);
    }
    if (completedStreamKeysRef.current.has(indexKey)) return false;

    if (entryIndex != null) {
      const existing = transcriptRef.current[entryIndex];
      if (!existing) {
        transcriptStreamIndexRef.current.delete(indexKey);
      } else {
        const finalValue = normalizedFinalText || existing.text.trim();
        if (!finalValue) {
          removeStreamingTranscript(role, normalizedKey);
          return false;
        }

        const updated = [...transcriptRef.current];
        updated[entryIndex] = {
          ...existing,
          text: finalValue,
          isStreaming: false,
          streamKey: undefined,
          timestamp: Date.now(),
        };
        let finalTextForSignals = finalValue;
        let removedFinalizedUserIndex = false;
        if (role === 'user' && entryIndex > 0) {
          const previous = updated[entryIndex - 1];
          if (previous?.role === 'user' && !previous.isStreaming) {
            const merged = mergeTranscriptText(previous.text, finalValue);
            updated[entryIndex - 1] = {
              ...previous,
              text: merged,
              timestamp: Date.now(),
            };
            updated.splice(entryIndex, 1);
            finalTextForSignals = merged;
            removedFinalizedUserIndex = true;
          }
        }
        transcriptRef.current = updated;
        setTranscript(updated);
        transcriptStreamIndexRef.current.delete(indexKey);
        if (removedFinalizedUserIndex) {
          refreshStreamIndicesFrom(entryIndex);
        }
        completedStreamKeysRef.current.add(indexKey);
        handleTranscriptIntentSignals(role, finalTextForSignals, Date.now());
        if (role === 'assistant') {
          assistantStreamChannelRef.current.delete(normalizedKey);
          assistantStreamingKeyRef.current = null;
        }
        if (role === 'user') userStreamingKeyRef.current = null;
        return true;
      }
    }

    if (!normalizedFinalText) return false;
    if (role === 'user') {
      const lastEntry = transcriptRef.current[transcriptRef.current.length - 1];
      if (lastEntry?.role === 'user' && !lastEntry.isStreaming) {
        const merged = mergeTranscriptText(lastEntry.text, normalizedFinalText);
        const updated = [...transcriptRef.current];
        updated[updated.length - 1] = {
          ...lastEntry,
          text: merged,
          timestamp: Date.now(),
        };
        transcriptRef.current = updated;
        setTranscript(updated);
        handleTranscriptIntentSignals(role, merged, Date.now());
      } else {
        appendTranscript(role, normalizedFinalText);
      }
    } else {
      appendTranscript(role, normalizedFinalText);
    }
    completedStreamKeysRef.current.add(indexKey);
    if (role === 'assistant') {
      assistantStreamChannelRef.current.delete(normalizedKey);
      assistantStreamingKeyRef.current = null;
    }
    if (role === 'user') userStreamingKeyRef.current = null;
    return true;
  }

  function shouldHandleAssistantChannel(streamKey: string, channel: 'audio' | 'text'): boolean {
    const existing = assistantStreamChannelRef.current.get(streamKey);
    if (!existing) {
      assistantStreamChannelRef.current.set(streamKey, channel);
      return true;
    }
    if (existing === channel) return true;
    if (channel === 'audio') {
      assistantStreamChannelRef.current.set(streamKey, 'audio');
      return true;
    }
    return false;
  }

  function assistantTurnKey(msg: Record<string, unknown>): string | null {
    const responseId = typeof msg.response_id === 'string' ? msg.response_id : null;
    const itemId = typeof msg.item_id === 'string' ? msg.item_id : null;
    const outputIndex = typeof msg.output_index === 'number' ? String(msg.output_index) : null;
    if (!responseId && !itemId && !outputIndex) return null;
    return `${responseId ?? 'no-response'}:${itemId ?? 'no-item'}:${outputIndex ?? 'no-index'}`;
  }

  function finalizeAssistantStreaming(msg: Record<string, unknown>, text: string): boolean {
    const normalized = text.trim();
    if (!normalized) return false;
    const streamKey = resolveAssistantStreamKey(msg);
    return finalizeStreamingTranscript('assistant', streamKey, normalized);
  }

  function flushUserPartialTranscript() {
    const fallbackText = inputPartialRef.current.trim();
    if (!fallbackText) return;
    const streamKey = userStreamingKeyRef.current ?? resolveUserStreamKey();
    finalizeStreamingTranscript('user', streamKey, fallbackText);
    inputPartialRef.current = '';
    userStreamingKeyRef.current = null;
  }

  function flushAssistantPartialTranscript(msg?: Record<string, unknown>) {
    const fallbackText = outputPartialRef.current.trim();
    if (!fallbackText) return;
    const streamKey = resolveAssistantStreamKey(msg);
    finalizeStreamingTranscript('assistant', streamKey, fallbackText);
    outputPartialRef.current = '';
    assistantStreamingKeyRef.current = null;
  }

  function parseInsightArgs(rawArgs: unknown): {
    type?: string;
    content?: string;
    confidence?: number;
  } | null {
    if (!rawArgs) return null;
    if (typeof rawArgs === 'string') {
      try {
        return JSON.parse(rawArgs) as { type?: string; content?: string; confidence?: number };
      } catch {
        return null;
      }
    }
    if (typeof rawArgs === 'object') {
      return rawArgs as { type?: string; content?: string; confidence?: number };
    }
    return null;
  }

  function scheduleTurnNudge() {
    const provider = activeProviderRef.current;
    // For OpenAI/ElevenLabs we trust server VAD so we don't force fake turns.
    if (provider === 'openai' || provider === 'elevenlabs') return;
    if (turnNudgeTimerRef.current) clearTimeout(turnNudgeTimerRef.current);
    turnNudgeTimerRef.current = setTimeout(() => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      if (!userAudioSinceLastTurnRef.current) return;
      if (isAssistantSpeakingRef.current) return;

      // Don't nudge if a response just finished — prevents echo-triggered loops
      if (Date.now() - lastResponseDoneAtRef.current < RESPONSE_COOLDOWN_MS) return;

      // Don't nudge if the model is stuck repeating itself
      if (consecutiveDuplicateCountRef.current >= DUPLICATE_THRESHOLD) return;

      flushSendBuffer();
      if (provider === 'grok') {
        ws.send(JSON.stringify({ type: 'input_audio_buffer.commit' }));
        ws.send(JSON.stringify({ type: 'response.create' }));
      } else {
        ws.send(JSON.stringify({ clientContent: { turnComplete: true } }));
      }
      console.log(`[${provider}] turn nudge sent`);
    }, TURN_NUDGE_MS);
  }

  // ── WebSocket message handler ──────────────────────────────────────────────

  async function handleOpenAiMessage(msg: Record<string, unknown>) {
    const type = msg.type as string | undefined;
    if (!type) return;
    logVoiceDebug(`[openai event] ${type}`);

    if (type === 'response.created') {
      openAiResponseInProgressRef.current = true;
      const response = msg.response as Record<string, unknown> | undefined;
      const responseId = typeof response?.id === 'string'
        ? response.id
        : (typeof msg.response_id === 'string' ? msg.response_id : null);
      if (responseId) {
        assistantStreamingKeyRef.current = responseId;
      }
    }

    // Assistant audio stream
    // GA Realtime API renames `response.audio.delta` → `response.output_audio.delta`.
    // Accept both for back-compat across model snapshots.
    if (
      (type === 'response.audio.delta' || type === 'response.output_audio.delta')
      && typeof msg.delta === 'string'
    ) {
      enqueuePlayback(msg.delta);
    }

    // User transcript deltas/finals
    if (type === 'input_audio_buffer.speech_started') {
      if (isWithinAssistantEchoGuard()) return;
      const streamKey = resolveUserStreamKey(msg);
      // Ensure the user bubble appears immediately, even before transcription deltas arrive.
      upsertStreamingTranscript('user', streamKey, inputPartialRef.current.trim() || '...');
    }
    if (type === 'conversation.item.input_audio_transcription.delta' && typeof msg.delta === 'string') {
      if (isWithinAssistantEchoGuard()) return;
      inputPartialRef.current += msg.delta;
      const streamKey = resolveUserStreamKey(msg);
      upsertStreamingTranscript('user', streamKey, inputPartialRef.current);
    }
    if (type === 'conversation.item.input_audio_transcription.completed' && typeof msg.transcript === 'string') {
      const streamKey = resolveUserStreamKey(msg);
      const transcript = msg.transcript.trim();
      if (transcript) {
        if (isWithinAssistantEchoGuard()) {
          removeStreamingTranscript('user', streamKey);
          inputPartialRef.current = '';
          userStreamingKeyRef.current = null;
          return;
        }
        if (activeProviderRef.current === 'elevenlabs' && !hasRecentLocalSpeechEnergy()) {
          const words = transcript.split(/\s+/).filter(Boolean).length;
          if (words <= 2) {
            removeStreamingTranscript('user', streamKey);
            inputPartialRef.current = '';
            userStreamingKeyRef.current = null;
            return;
          }
        }
        finalizeStreamingTranscript('user', streamKey, transcript);
      } else {
        flushUserPartialTranscript();
      }
      inputPartialRef.current = '';
      userStreamingKeyRef.current = null;
    }
    if (type === 'conversation.item.input_audio_transcription.failed') {
      const streamKey = resolveUserStreamKey(msg);
      removeStreamingTranscript('user', streamKey);
      inputPartialRef.current = '';
      userStreamingKeyRef.current = null;
      logVoiceDebug('input audio transcription failed', msg);
      const transcriptionError = (msg.error as Record<string, unknown> | undefined) ?? null;
      logVoiceDebugJson('input_audio_transcription.failed details', {
        event_id: msg.event_id,
        item_id: msg.item_id,
        content_index: msg.content_index,
        error: transcriptionError,
      });
    }

    // Assistant transcript deltas/finals
    // GA Realtime: response.audio_transcript.* → response.output_audio_transcript.*.
    if (
      (type === 'response.audio_transcript.delta' || type === 'response.output_audio_transcript.delta')
      && typeof msg.delta === 'string'
    ) {
      const streamKey = resolveAssistantStreamKey(msg);
      if (!shouldHandleAssistantChannel(streamKey, 'audio')) return;
      outputPartialRef.current += msg.delta;
      upsertStreamingTranscript('assistant', streamKey, outputPartialRef.current);
    }
    if (
      (type === 'response.audio_transcript.done' || type === 'response.output_audio_transcript.done')
      && typeof msg.transcript === 'string'
    ) {
      const streamKey = resolveAssistantStreamKey(msg);
      if (!shouldHandleAssistantChannel(streamKey, 'audio')) return;
      const finalText = msg.transcript.trim() || outputPartialRef.current.trim();
      if (finalText) {
        const appended = finalizeAssistantStreaming(msg, finalText);
        if (appended && activeProviderRef.current === 'elevenlabs') {
          try {
            const tts = await callSynthesizeSpeech(finalText, {
              voiceId: elevenVoiceIdRef.current,
              sessionId: sessionIdRef.current ?? undefined,
            });
            enqueuePlayback(tts.audioBase64, tts.sampleRate);
          } catch (err) {
            console.error('ElevenLabs synthesis failed:', err);
          }
        }
      }
      outputPartialRef.current = '';
      assistantStreamingKeyRef.current = null;
    }

    // Text-only output path (used by elevenlabs transport mode)
    if (type === 'response.text.delta' && typeof msg.delta === 'string') {
      const streamKey = resolveAssistantStreamKey(msg);
      if (!shouldHandleAssistantChannel(streamKey, 'text')) return;
      outputPartialRef.current += msg.delta;
      upsertStreamingTranscript('assistant', streamKey, outputPartialRef.current);
    }
    if (type === 'response.text.done') {
      const streamKey = resolveAssistantStreamKey(msg);
      if (!shouldHandleAssistantChannel(streamKey, 'text')) return;
      const textCandidate =
        (typeof msg.text === 'string' ? msg.text : '') ||
        outputPartialRef.current.trim();
      if (textCandidate) {
        const appended = finalizeAssistantStreaming(msg, textCandidate);
        if (appended && activeProviderRef.current === 'elevenlabs') {
          try {
            const tts = await callSynthesizeSpeech(textCandidate, {
              voiceId: elevenVoiceIdRef.current,
              sessionId: sessionIdRef.current ?? undefined,
            });
            enqueuePlayback(tts.audioBase64, tts.sampleRate);
          } catch (err) {
            console.error('ElevenLabs synthesis failed:', err);
          }
        }
      }
      outputPartialRef.current = '';
      assistantStreamingKeyRef.current = null;
    }

    // Fallback flush if the response closes without transcript.done
    if (type === 'response.done') {
      const response = msg.response as Record<string, unknown> | undefined;
      addVoiceUsageDelta(extractOpenAiUsageDelta(msg));
      const status = typeof response?.status === 'string' ? response.status : 'unknown';
      if (status !== 'completed') {
        logVoiceDebug('response.done with non-completed status', response);
        logVoiceDebugJson('response.done failure details', {
          status,
          status_details: response?.status_details ?? null,
          output: response?.output ?? null,
        });
      }
      flushUserPartialTranscript();
      flushAssistantPartialTranscript(msg);
      userAudioSinceLastTurnRef.current = false;
      lastResponseDoneAtRef.current = Date.now();
      openAiResponseInProgressRef.current = false;
      if (shouldApplyEchoGuard()) {
        const echoGuardMs = getActiveEchoGuardMs();
        suppressUserAudioUntilRef.current = Math.max(
          suppressUserAudioUntilRef.current,
          Date.now() + echoGuardMs
        );
      }
      if (turnNudgeTimerRef.current) clearTimeout(turnNudgeTimerRef.current);
      if (queuedOpenAiResponseCreateRef.current) {
        queuedOpenAiResponseCreateRef.current = false;
        requestOpenAiResponse();
      }
    }

    // Optional realtime extraction via function calls
    if (type === 'response.output_item.done') {
      const item = msg.item as Record<string, unknown> | undefined;
      if (item?.type === 'function_call' && item.name === 'add_insight') {
        const args = parseInsightArgs(item.arguments);
        if (args?.type && args?.content && typeof args.confidence === 'number') {
          const extraction: RealTimeExtraction = {
            type: args.type as RealTimeExtractionType,
            content: args.content,
            confidence: args.confidence,
            timestamp: Date.now(),
          };
          setRealtimeExtractions(prev => {
            if (prev.some(e => e.content === extraction.content)) return prev;
            return [...prev, extraction];
          });
        }

        // Ack tool call so model can continue.
        const callId = item.call_id;
        if (typeof callId === 'string' && wsRef.current?.readyState === WebSocket.OPEN) {
          if (!handledToolCallIdsRef.current.has(callId)) {
            handledToolCallIdsRef.current.add(callId);
            wsRef.current.send(
              JSON.stringify({
                type: 'conversation.item.create',
                item: {
                  type: 'function_call_output',
                  call_id: callId,
                  output: JSON.stringify({ result: 'recorded' }),
                },
              })
            );
            requestOpenAiResponse();
          }
        }
      }
    }

    // Newer OpenAI Realtime function-call event shape (also seen via ElevenLabs transport mode)
    if (type === 'response.function_call_arguments.done' && msg.name === 'add_insight') {
      const args = parseInsightArgs(msg.arguments);
      if (args?.type && args?.content && typeof args.confidence === 'number') {
        const extraction: RealTimeExtraction = {
          type: args.type as RealTimeExtractionType,
          content: args.content,
          confidence: args.confidence,
          timestamp: Date.now(),
        };
        setRealtimeExtractions(prev => {
          if (prev.some(e => e.content === extraction.content)) return prev;
          return [...prev, extraction];
        });
      }

      const callId = msg.call_id;
      if (typeof callId === 'string' && wsRef.current?.readyState === WebSocket.OPEN) {
        if (!handledToolCallIdsRef.current.has(callId)) {
          handledToolCallIdsRef.current.add(callId);
          wsRef.current.send(
            JSON.stringify({
              type: 'conversation.item.create',
              item: {
                type: 'function_call_output',
                call_id: callId,
                output: JSON.stringify({ result: 'recorded' }),
              },
            })
          );
          requestOpenAiResponse();
        }
      }
    }

    if (type === 'error') {
      logVoiceDebug('openai error event', msg);
      const err = (msg.error as Record<string, unknown> | undefined)?.message;
      const errorMessage = typeof err === 'string' ? err : 'Voice runtime error';
      if (errorMessage.includes('active response in progress')) {
        openAiResponseInProgressRef.current = true;
        queuedOpenAiResponseCreateRef.current = true;
        console.warn('[voice] response.create queued while active response in progress');
      } else {
        setError(errorMessage);
      }
    }
  }

  function sanitizeGeminiTranscript(text: string): string {
    return text
      .replace(/<[^>]*>/g, '')       // strip HTML/XML-like tags (e.g. <ctrl46>)
      .replace(/\[ctrl\d+\]/gi, '')  // strip [ctrl46]-style sequences
      .replace(/\u0000/g, '')        // strip null bytes
      .trim();
  }

  function geminiUserStreamKey(): string {
    if (userStreamingKeyRef.current) return userStreamingKeyRef.current;
    const key = `gemini-user-${++geminiTurnCounter}`;
    userStreamingKeyRef.current = key;
    return key;
  }

  function geminiAssistantStreamKey(): string {
    if (assistantStreamingKeyRef.current) return assistantStreamingKeyRef.current;
    const key = `gemini-assistant-${++geminiTurnCounter}`;
    assistantStreamingKeyRef.current = key;
    return key;
  }

  function clearGeminiStallTimer() {
    if (geminiStallTimerRef.current) {
      clearTimeout(geminiStallTimerRef.current);
      geminiStallTimerRef.current = null;
    }
    geminiStallRetriesRef.current = 0;
  }

  function markGeminiActivity() {
    geminiLastActivityRef.current = Date.now();
    geminiStallRetriesRef.current = 0;
  }

  function scheduleGeminiStallRecovery() {
    if (geminiStallTimerRef.current) clearTimeout(geminiStallTimerRef.current);
    geminiStallRetriesRef.current = 0;
    geminiLastActivityRef.current = Date.now();

    const attemptRecovery = () => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      if (activeProviderRef.current !== 'gemini') return;

      const sinceLastActivity = Date.now() - geminiLastActivityRef.current;
      if (sinceLastActivity < GEMINI_STALL_TIMEOUT_MS) {
        geminiStallTimerRef.current = setTimeout(attemptRecovery, GEMINI_STALL_TIMEOUT_MS - sinceLastActivity + 200);
        return;
      }

      if (isAssistantSpeakingRef.current) {
        geminiStallTimerRef.current = setTimeout(attemptRecovery, GEMINI_STALL_RETRY_MS);
        return;
      }

      if (geminiStallRetriesRef.current >= GEMINI_STALL_MAX_RETRIES) {
        console.warn('[gemini] stall recovery exhausted after', geminiStallRetriesRef.current, 'retries');
        geminiStallTimerRef.current = null;
        return;
      }

      const hasActiveAssistantStream = assistantStreamingKeyRef.current != null;
      if (hasActiveAssistantStream) {
        geminiStallTimerRef.current = setTimeout(attemptRecovery, GEMINI_STALL_RETRY_MS);
        return;
      }

      geminiStallRetriesRef.current++;
      console.log(`[gemini] stall recovery attempt ${geminiStallRetriesRef.current} — sending turnComplete nudge`);
      flushSendBuffer();
      ws.send(JSON.stringify({ clientContent: { turnComplete: true } }));

      geminiStallTimerRef.current = setTimeout(attemptRecovery, GEMINI_STALL_RETRY_MS);
    };

    geminiStallTimerRef.current = setTimeout(attemptRecovery, GEMINI_STALL_TIMEOUT_MS);
  }

  function handleGeminiMessage(msg: Record<string, unknown>) {
    addVoiceUsageDelta(extractGeminiUsageDelta(msg));
    const topLevelInTx = msg.inputTranscription as Record<string, unknown> | undefined;
    const topLevelOutTx = msg.outputTranscription as Record<string, unknown> | undefined;
    if (typeof topLevelInTx?.text === 'string') {
      inputPartialRef.current += topLevelInTx.text;
      const sanitizedInput = sanitizeGeminiTranscript(inputPartialRef.current);
      if (sanitizedInput) upsertStreamingTranscript('user', geminiUserStreamKey(), sanitizedInput);
    }
    if (typeof topLevelOutTx?.text === 'string') {
      markGeminiActivity();
      const streamKey = geminiAssistantStreamKey();
      outputPartialRef.current += topLevelOutTx.text;
      const sanitizedOutput = sanitizeGeminiTranscript(outputPartialRef.current);
      if (sanitizedOutput) upsertStreamingTranscript('assistant', streamKey, sanitizedOutput);
    }

    if (msg.serverContent) {
      const sc = msg.serverContent as Record<string, unknown>;

      // Play audio parts — also resets stall timer since Gemini is responding
      const modelTurn = sc.modelTurn as Record<string, unknown> | undefined;
      if (modelTurn?.parts) {
        markGeminiActivity();
        for (const part of modelTurn.parts as Array<Record<string, unknown>>) {
          const inlineData = part.inlineData as Record<string, unknown> | undefined;
          if (inlineData?.data) {
            enqueuePlayback(inlineData.data as string);
          }
        }
      }

      // Accumulate transcriptions
      const inTx = (
        sc.inputTranscription ?? sc.input_transcription
      ) as Record<string, unknown> | undefined;
      const outTx = (
        sc.outputTranscription ?? sc.output_transcription
      ) as Record<string, unknown> | undefined;
      if (typeof inTx?.text === 'string') {
        inputPartialRef.current += inTx.text;
        const sanitizedInput = sanitizeGeminiTranscript(inputPartialRef.current);
        if (sanitizedInput) upsertStreamingTranscript('user', geminiUserStreamKey(), sanitizedInput);
      }
      if (typeof outTx?.text === 'string') {
        markGeminiActivity();
        const streamKey = geminiAssistantStreamKey();
        outputPartialRef.current += outTx.text;
        const sanitizedOutput = sanitizeGeminiTranscript(outputPartialRef.current);
        if (sanitizedOutput) upsertStreamingTranscript('assistant', streamKey, sanitizedOutput);
      }

      // Flush transcript on turn complete
      if (sc.turnComplete) {
        const finalUserText = sanitizeGeminiTranscript(inputPartialRef.current);
        if (finalUserText) {
          finalizeStreamingTranscript('user', geminiUserStreamKey(), finalUserText);
        } else if (userStreamingKeyRef.current) {
          removeStreamingTranscript('user', geminiUserStreamKey());
        }
        inputPartialRef.current = '';
        userStreamingKeyRef.current = null;

        const finalAssistantText = sanitizeGeminiTranscript(outputPartialRef.current);
        if (finalAssistantText) {
          finalizeStreamingTranscript('assistant', geminiAssistantStreamKey(), finalAssistantText);
        } else if (assistantStreamingKeyRef.current) {
          removeStreamingTranscript('assistant', geminiAssistantStreamKey());
        }
        outputPartialRef.current = '';
        assistantStreamingKeyRef.current = null;
        lastResponseDoneAtRef.current = Date.now();
        userAudioSinceLastTurnRef.current = false;
        if (turnNudgeTimerRef.current) clearTimeout(turnNudgeTimerRef.current);
        clearGeminiStallTimer();
      }

      // Interrupted — reset playback queue and partial text
      if (sc.interrupted) {
        nextPlayTimeRef.current = 0;
        if (inputPartialRef.current.trim()) {
          finalizeStreamingTranscript('user', geminiUserStreamKey(), inputPartialRef.current.trim());
        }
        inputPartialRef.current = '';
        userStreamingKeyRef.current = null;
        outputPartialRef.current = '';
        assistantStreamingKeyRef.current = null;
        setIsAssistantSpeaking(false);
        isAssistantSpeakingRef.current = false;
        if (speakingTimerRef.current) clearTimeout(speakingTimerRef.current);
        if (turnNudgeTimerRef.current) clearTimeout(turnNudgeTimerRef.current);
        clearGeminiStallTimer();
      }
    }

    if (msg.toolCall || msg.tool_call) {
      const tc = (msg.toolCall ?? msg.tool_call) as Record<string, unknown>;
      const functionCalls = (tc.functionCalls ?? tc.function_calls) as Array<Record<string, unknown>> | undefined;
      if (!functionCalls) return;

      const responses: Array<{ id: string; name: string; response: Record<string, unknown> }> = [];

      for (const fc of functionCalls) {
        const id = typeof fc.id === 'string' ? fc.id : null;
        const name = typeof fc.name === 'string' ? fc.name : null;
        if (!id || !name) continue;

        let args: { type?: string; content?: string; confidence?: number } | undefined;
        if (fc.args && typeof fc.args === 'object') {
          args = fc.args as { type?: string; content?: string; confidence?: number };
        } else if (typeof fc.args === 'string') {
          try {
            args = JSON.parse(fc.args) as { type?: string; content?: string; confidence?: number };
          } catch {
            args = undefined;
          }
        }

        if (name === 'add_insight' && args?.type && args?.content && args?.confidence != null) {
          const extraction: RealTimeExtraction = {
            type: args.type as RealTimeExtractionType,
            content: args.content,
            confidence: args.confidence,
            timestamp: Date.now(),
          };
          setRealtimeExtractions(prev => {
            if (prev.some(e => e.content === extraction.content)) return prev;
            return [...prev, extraction];
          });
        }

        responses.push({ id, name, response: { result: 'recorded' } });
      }

      if (responses.length > 0) {
        wsRef.current?.send(JSON.stringify({ toolResponse: { functionResponses: responses } }));
      }
    }
  }

  function handleGrokMessage(msg: Record<string, unknown>) {
    const type = msg.type as string | undefined;
    if (!type) return;

    // Grok: response.output_audio.delta (vs OpenAI's response.audio.delta)
    if (type === 'response.output_audio.delta' && typeof msg.delta === 'string') {
      enqueuePlayback(msg.delta);
    }

    // User transcript (same event name as OpenAI)
    if (type === 'conversation.item.input_audio_transcription.completed' && typeof msg.transcript === 'string') {
      const streamKey = resolveUserStreamKey(msg);
      const transcript = msg.transcript.trim();
      if (transcript) {
        if (isWithinAssistantEchoGuard()) {
          removeStreamingTranscript('user', streamKey);
          inputPartialRef.current = '';
          userStreamingKeyRef.current = null;
          return;
        }
        if (activeProviderRef.current === 'elevenlabs' && !hasRecentLocalSpeechEnergy()) {
          const words = transcript.split(/\s+/).filter(Boolean).length;
          if (words <= 2) {
            removeStreamingTranscript('user', streamKey);
            inputPartialRef.current = '';
            userStreamingKeyRef.current = null;
            return;
          }
        }
        finalizeStreamingTranscript('user', streamKey, transcript);
        // Real user speech resets the duplicate guard
        consecutiveDuplicateCountRef.current = 0;
        lastAssistantResponseTextRef.current = '';
      } else {
        flushUserPartialTranscript();
      }
      inputPartialRef.current = '';
      userStreamingKeyRef.current = null;
    }

    // Grok: response.output_audio_transcript.delta
    if (type === 'response.output_audio_transcript.delta' && typeof msg.delta === 'string') {
      const streamKey = resolveAssistantStreamKey(msg);
      outputPartialRef.current += msg.delta;
      upsertStreamingTranscript('assistant', streamKey, outputPartialRef.current);
    }

    // Grok: response.output_audio_transcript.done
    if (type === 'response.output_audio_transcript.done' && typeof msg.transcript === 'string') {
      const streamKey = resolveAssistantStreamKey(msg);
      const finalText = msg.transcript.trim() || outputPartialRef.current.trim();
      if (finalText) {
        // Track consecutive duplicate responses
        if (finalText === lastAssistantResponseTextRef.current) {
          consecutiveDuplicateCountRef.current++;
          if (consecutiveDuplicateCountRef.current >= DUPLICATE_THRESHOLD) {
            console.warn(`[grok] suppressing duplicate response (${consecutiveDuplicateCountRef.current}x): "${finalText.slice(0, 60)}..."`);
            outputPartialRef.current = '';
            return;
          }
        } else {
          consecutiveDuplicateCountRef.current = 0;
        }
        lastAssistantResponseTextRef.current = finalText;
        finalizeStreamingTranscript('assistant', streamKey, finalText);
      }
      outputPartialRef.current = '';
      assistantStreamingKeyRef.current = null;
    }

    if (type === 'response.done') {
      addVoiceUsageDelta(extractOpenAiUsageDelta(msg));
      flushUserPartialTranscript();
      flushAssistantPartialTranscript(msg);
      userAudioSinceLastTurnRef.current = false;
      lastResponseDoneAtRef.current = Date.now();
      if (shouldApplyEchoGuard()) {
        const echoGuardMs = getActiveEchoGuardMs();
        suppressUserAudioUntilRef.current = Math.max(
          suppressUserAudioUntilRef.current,
          Date.now() + echoGuardMs
        );
      }
      if (turnNudgeTimerRef.current) clearTimeout(turnNudgeTimerRef.current);
    }

    // Grok: response.function_call_arguments.done (vs OpenAI's response.output_item.done)
    if (type === 'response.function_call_arguments.done') {
      if (msg.name === 'add_insight' && typeof msg.arguments === 'string') {
        try {
          const args = JSON.parse(msg.arguments as string) as {
            type?: string;
            content?: string;
            confidence?: number;
          };
          if (args.type && args.content && typeof args.confidence === 'number') {
            const extraction: RealTimeExtraction = {
              type: args.type as RealTimeExtractionType,
              content: args.content,
              confidence: args.confidence,
              timestamp: Date.now(),
            };
            setRealtimeExtractions(prev => {
              if (prev.some(e => e.content === extraction.content)) return prev;
              return [...prev, extraction];
            });
          }

          const callId = msg.call_id;
          if (typeof callId === 'string' && wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(
              JSON.stringify({
                type: 'conversation.item.create',
                item: {
                  type: 'function_call_output',
                  call_id: callId,
                  output: JSON.stringify({ result: 'recorded' }),
                },
              })
            );
            wsRef.current.send(JSON.stringify({ type: 'response.create' }));
          }
        } catch {
          // Ignore malformed tool arguments
        }
      }
    }

    if (type === 'error') {
      const err = (msg.error as Record<string, unknown> | undefined)?.message;
      setError(typeof err === 'string' ? err : 'Grok voice runtime error');
    }
  }

  function handleMessage(event: MessageEvent) {
    void (async () => {
      const msg = await parseWsJson(event.data);
      if (!msg) return;
      if (activeProviderRef.current === 'openai' || activeProviderRef.current === 'elevenlabs') {
        await handleOpenAiMessage(msg);
      } else if (activeProviderRef.current === 'gemini') {
        handleGeminiMessage(msg);
      } else if (activeProviderRef.current === 'grok') {
        handleGrokMessage(msg);
      }
    })();
  }

  const suppressVoiceRuntime = useCallback(() => {
    intentionalCloseRef.current = true;

    workletNodeRef.current?.disconnect();
    workletNodeRef.current = null;

    mediaStreamRef.current?.getTracks().forEach(track => track.stop());
    mediaStreamRef.current = null;

    sendBufferRef.current = [];
    sendBufferLenRef.current = 0;

    if (turnNudgeTimerRef.current) {
      clearTimeout(turnNudgeTimerRef.current);
      turnNudgeTimerRef.current = null;
    }
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    if (speakingTimerRef.current) {
      clearTimeout(speakingTimerRef.current);
      speakingTimerRef.current = null;
    }
    if (autoEndTimerRef.current) {
      clearTimeout(autoEndTimerRef.current);
      autoEndTimerRef.current = null;
    }
    clearGeminiStallTimer();

    wsRef.current?.close();
    wsRef.current = null;

    const captureCtx = captureCtxRef.current;
    captureCtxRef.current = null;
    if (captureCtx) void captureCtx.close();

    const playbackCtx = playbackCtxRef.current;
    playbackCtxRef.current = null;
    if (playbackCtx) void playbackCtx.close();

    nextPlayTimeRef.current = 0;
    userAudioSinceLastTurnRef.current = false;
    suppressUserAudioUntilRef.current = 0;
    lastLocalSpeechAtRef.current = 0;
    activeModelRef.current = 'gpt-realtime-2';
    openAiResponseInProgressRef.current = false;
    queuedOpenAiResponseCreateRef.current = false;
    handledToolCallIdsRef.current = new Set();
    inputPartialRef.current = '';
    outputPartialRef.current = '';
    userStreamingKeyRef.current = null;
    assistantStreamingKeyRef.current = null;
    transcriptStreamIndexRef.current = new Map();
    completedStreamKeysRef.current = new Set();
    assistantStreamChannelRef.current = new Map();
    voiceUsageTotalsRef.current = createEmptyVoiceUsageTotals();
    sessionStartedAtRef.current = 0;
  }, []);

  useEffect(() => {
    isUnmountedRef.current = false;
    return () => {
      isUnmountedRef.current = true;
      suppressVoiceRuntime();
    };
  }, [suppressVoiceRuntime]);

  // ── Session lifecycle ──────────────────────────────────────────────────────

  const startSession = useCallback(
    async (
      sessionType: SessionType,
      options?: { continueSessionId?: string; seededTranscript?: TranscriptEntry[] }
    ) => {
      if (!userId || startingRef.current) return;
      startingRef.current = true;
      intentionalCloseRef.current = false;
      const seededTranscript = options?.seededTranscript ?? [];
      const seededTranscriptForModel = seededTranscript
        .filter(entry => entry.text.trim().length > 0)
        .slice(-RESUME_SEED_MESSAGE_LIMIT);
      setStatus('connecting');
      setSessionId(options?.continueSessionId ?? null);
      setError(null);
      setTranscript(seededTranscript);
      transcriptRef.current = seededTranscript;
      persistedTranscriptOffsetRef.current = seededTranscript.length;
      setRealtimeExtractions([]);
      setExtractionSummary(null);
      setIsAssistantSpeaking(false);
      isAssistantSpeakingRef.current = false;
      consecutiveDuplicateCountRef.current = 0;
      lastAssistantResponseTextRef.current = '';
      lastResponseDoneAtRef.current = 0;
      lastUserEndIntentAtRef.current = 0;
      lastCoachFarewellAtRef.current = 0;
      userEndIntentDetectedRef.current = false;
      if (autoEndTimerRef.current) clearTimeout(autoEndTimerRef.current);
      inputPartialRef.current = '';
      outputPartialRef.current = '';
      userStreamingKeyRef.current = null;
      assistantStreamingKeyRef.current = null;
      transcriptStreamIndexRef.current = new Map();
      completedStreamKeysRef.current = new Set();
      assistantStreamChannelRef.current = new Map();
      userAudioSinceLastTurnRef.current = false;
      suppressUserAudioUntilRef.current = 0;
      lastLocalSpeechAtRef.current = 0;
      openAiResponseInProgressRef.current = false;
      queuedOpenAiResponseCreateRef.current = false;
      handledToolCallIdsRef.current = new Set();
      resetVoiceUsageTracking();
      if (turnNudgeTimerRef.current) clearTimeout(turnNudgeTimerRef.current);
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);

      try {
        // 1. Get ephemeral token + session from backend
        const {
          ephemeralToken,
          sessionId,
          provider: responseProvider,
          model,
          voice,
          systemPrompt,
        } = await callGetVoiceToken(sessionType, undefined, options?.continueSessionId);
        if (isUnmountedRef.current) {
          suppressVoiceRuntime();
          return;
        }
        const provider: VoiceProvider = responseProvider ?? 'openai';
        logVoiceDebug('token acquired', {
          provider,
          model,
          voice,
          sessionType,
          continueSession: Boolean(options?.continueSessionId),
        });
        sessionIdRef.current = sessionId;
        setSessionId(sessionId);
        activeProviderRef.current = provider;
        activeModelRef.current = model;
        elevenVoiceIdRef.current = provider === 'elevenlabs' ? voice : undefined;

        // 2. Set up audio contexts
        captureCtxRef.current = new AudioContext({ sampleRate: SEND_SAMPLE_RATE });
        playbackCtxRef.current = new AudioContext();
        nextPlayTimeRef.current = 0;
        await captureCtxRef.current.resume();
        await playbackCtxRef.current.resume();

        // 3. Load AudioWorklet module
        await captureCtxRef.current.audioWorklet.addModule('/audio-processor.js');

        // 4. Open provider WebSocket
        const ws =
          provider === 'openai' || provider === 'elevenlabs'
            ? new WebSocket(
                `${OPENAI_WS_URL}?model=${encodeURIComponent(model)}`,
                [
                  'realtime',
                  `openai-insecure-api-key.${ephemeralToken}`,
                ]
              )
            : provider === 'gemini'
              ? new WebSocket(
                  `${GEMINI_WS_URL}?access_token=${encodeURIComponent(ephemeralToken)}`
                )
              : provider === 'grok'
                ? new WebSocket(GROK_WS_URL, [
                    `xai-client-secret.${ephemeralToken}`,
                  ])
                : null;
        if (!ws) {
          throw new Error(`Voice provider "${provider}" is not yet supported in the client`);
        }
        const socket = ws;
        socket.binaryType = 'arraybuffer';
        wsRef.current = socket;

        socket.onerror = () => {
          logVoiceDebug('websocket error', { provider: activeProviderRef.current });
          if (!intentionalCloseRef.current) {
            setError('Voice connection error. Please try again.');
            setStatus('error');
          }
        };
        socket.onclose = (event) => {
          logVoiceDebug('websocket closed', {
            provider: activeProviderRef.current,
            code: event.code,
            reason: event.reason || 'none',
          });
          if (intentionalCloseRef.current) return;

          const isGeminiServerError = activeProviderRef.current === 'gemini'
            && (event.code === 1011 || event.code === 1006);

          if (isGeminiServerError && !isUnmountedRef.current && !startingRef.current) {
            console.warn(`[gemini] server error (code=${event.code}), will show error`);
          }

          setError(
            `Voice connection closed (code=${event.code}, reason=${event.reason || 'none'})`
          );
          setStatus('error');
        };

        await new Promise<void>((resolve, reject) => {
          const timeout = setTimeout(() => {
            cleanup();
            reject(new Error('WebSocket connection timed out while opening'));
          }, WS_OPEN_TIMEOUT_MS);

          const onOpen = () => {
            cleanup();
            console.log(`[${provider}] WebSocket connected`);
            resolve();
          };
          const onErrorBeforeOpen = () => {
            cleanup();
            reject(new Error('WebSocket connection failed before opening'));
          };
          const onCloseBeforeOpen = (event: CloseEvent) => {
            cleanup();
            reject(
              new Error(
                `Voice connection closed before opening (code=${event.code}, reason=${event.reason || 'none'})`
              )
            );
          };

          function cleanup() {
            clearTimeout(timeout);
            socket.removeEventListener('open', onOpen);
            socket.removeEventListener('error', onErrorBeforeOpen);
            socket.removeEventListener('close', onCloseBeforeOpen);
          }

          socket.addEventListener('open', onOpen);
          socket.addEventListener('error', onErrorBeforeOpen);
          socket.addEventListener('close', onCloseBeforeOpen);
        });
        if (isUnmountedRef.current) {
          suppressVoiceRuntime();
          return;
        }

        if (provider === 'gemini') {
          const setupMsg = {
            setup: {
              model,
              generationConfig: {
                responseModalities: ['AUDIO'],
                speechConfig: {
                  voiceConfig: { prebuiltVoiceConfig: { voiceName: voice } },
                },
              },
            },
          };
          ws.send(JSON.stringify(setupMsg));

          await new Promise<void>((resolve, reject) => {
            const timeout = setTimeout(() => reject(new Error('Gemini setup timed out')), 15000);
            const onSetup = (e: MessageEvent) => {
              void (async () => {
                const setupFrame = await parseWsJson(e.data);
                if (!setupFrame) return;
                if (setupFrame.setupComplete) {
                  cleanup();
                  resolve();
                  return;
                }
                if (setupFrame.goAway || setupFrame.error) {
                  cleanup();
                  reject(new Error(`Gemini setup failed: ${JSON.stringify(setupFrame)}`));
                }
              })();
            };
            const onSetupClose = (event: CloseEvent) => {
              cleanup();
              reject(
                new Error(
                  `Gemini connection closed during setup (code=${event.code}, reason=${event.reason || 'none'})`
                )
              );
            };
            const onSetupError = () => {
              cleanup();
              reject(new Error('Gemini connection error during setup'));
            };
            const cleanup = () => {
              clearTimeout(timeout);
              ws.removeEventListener('message', onSetup);
              ws.removeEventListener('close', onSetupClose);
              ws.removeEventListener('error', onSetupError);
            };
            ws.addEventListener('message', onSetup);
            ws.addEventListener('close', onSetupClose);
            ws.addEventListener('error', onSetupError);
          });
        }

        if (provider === 'grok') {
          ws.send(
            JSON.stringify({
              type: 'session.update',
              session: {
                instructions: systemPrompt,
                voice,
                turn_detection: { type: 'server_vad' },
                audio: {
                  input: { format: { type: 'audio/pcm', rate: SEND_SAMPLE_RATE } },
                  output: { format: { type: 'audio/pcm', rate: RECV_SAMPLE_RATE } },
                },
                tools: [
                  {
                    type: 'function',
                    name: 'add_insight',
                    description:
                      'Record a notable belief, value, goal, action item, pattern, constraint or emotional state from the conversation.',
                    parameters: {
                      type: 'object',
                      properties: {
                        type: {
                          type: 'string',
                          enum: ['key_insight', 'value_stated', 'goal', 'action_item', 'contradiction'],
                        },
                        content: { type: 'string' },
                        confidence: { type: 'number' },
                      },
                      required: ['type', 'content', 'confidence'],
                    },
                  },
                ],
              },
            })
          );

          await new Promise<void>((resolve, reject) => {
            const timeout = setTimeout(() => reject(new Error('Grok session setup timed out')), 15000);
            const onSetup = (e: MessageEvent) => {
              void (async () => {
                const frame = await parseWsJson(e.data);
                if (!frame) return;
                if (frame.type === 'session.updated') {
                  cleanup();
                  resolve();
                  return;
                }
                if (frame.type === 'error') {
                  cleanup();
                  reject(new Error(`Grok setup failed: ${JSON.stringify(frame)}`));
                }
              })();
            };
            const onSetupClose = (event: CloseEvent) => {
              cleanup();
              reject(
                new Error(
                  `Grok connection closed during setup (code=${event.code}, reason=${event.reason || 'none'})`
                )
              );
            };
            const onSetupError = () => {
              cleanup();
              reject(new Error('Grok connection error during setup'));
            };
            const cleanup = () => {
              clearTimeout(timeout);
              ws.removeEventListener('message', onSetup);
              ws.removeEventListener('close', onSetupClose);
              ws.removeEventListener('error', onSetupError);
            };
            ws.addEventListener('message', onSetup);
            ws.addEventListener('close', onSetupClose);
            ws.addEventListener('error', onSetupError);
          });
        }

        ws.onmessage = handleMessage;
        setStatus('active');

        // Trigger the assistant to speak first so users are not waiting in silence.
        const kickoffPrompt = getVoiceKickoffPrompt(sessionType);
        const resumePrompt =
          'Continue this conversation from where it paused. Briefly acknowledge the last topic, then ask one focused follow-up question and stop. Let the user respond before you ask anything else — do not stack a second question.';
        const promptToSend = options?.continueSessionId ? resumePrompt : kickoffPrompt;
        if (provider === 'openai' || provider === 'elevenlabs' || provider === 'grok') {
          if (options?.continueSessionId && seededTranscriptForModel.length > 0) {
            logVoiceDebug('seeding transcript for resume', {
              provider,
              entries: seededTranscriptForModel.length,
            });
            for (const entry of seededTranscriptForModel) {
              ws.send(
                JSON.stringify({
                  type: 'conversation.item.create',
                  item: {
                    type: 'message',
                    role: entry.role,
                    content: [{ type: 'input_text', text: entry.text }],
                  },
                })
              );
            }
          }
          ws.send(
            JSON.stringify({
              type: 'conversation.item.create',
              item: {
                type: 'message',
                role: 'user',
                content: [{ type: 'input_text', text: promptToSend }],
              },
            })
          );
          logVoiceDebug('sent kickoff/resume prompt to model', {
            provider,
            promptType: options?.continueSessionId ? 'resume' : 'kickoff',
          });
          requestOpenAiResponse();
        } else if (provider === 'gemini') {
          const turns = options?.continueSessionId && seededTranscriptForModel.length > 0
            ? [
                ...seededTranscriptForModel.map(entry => ({
                    role: entry.role === 'assistant' ? 'model' : 'user',
                    parts: [{ text: entry.text }],
                  })),
                {
                  role: 'user',
                  parts: [{ text: promptToSend }],
                },
              ]
            : [
                {
                  role: 'user',
                  parts: [{ text: promptToSend }],
                },
              ];
          ws.send(
            JSON.stringify({
              clientContent: {
                turns,
                turnComplete: true,
              },
            })
          );
        }

        // 6. Start mic capture
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            channelCount: 1,
            sampleRate: SEND_SAMPLE_RATE,
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });
        if (isUnmountedRef.current) {
          stream.getTracks().forEach(track => track.stop());
          suppressVoiceRuntime();
          return;
        }
        mediaStreamRef.current = stream;

        const source = captureCtxRef.current.createMediaStreamSource(stream);
        const worklet = new AudioWorkletNode(captureCtxRef.current, 'pcm-processor');
        workletNodeRef.current = worklet;

        worklet.port.onmessage = (e: MessageEvent<ArrayBuffer>) => {
          if (isMutedRef.current) return;
          if (shouldApplyEchoGuard() && Date.now() < suppressUserAudioUntilRef.current) return;
          const chunk = new Float32Array(e.data);
          sendBufferRef.current.push(chunk);
          sendBufferLenRef.current += chunk.length;
          schedulePendingFlush();
          if (sendBufferLenRef.current >= FLUSH_SAMPLES) {
            if (flushTimerRef.current) {
              clearTimeout(flushTimerRef.current);
              flushTimerRef.current = null;
            }
            flushSendBuffer();
          }
        };

        source.connect(worklet);
        // Not connected to destination — we don't want mic playback

      } catch (err) {
        if (!isUnmountedRef.current) {
          setError(toUserFacingErrorMessage(err, 'Failed to start voice session'));
          setStatus('error');
        }
      } finally {
        startingRef.current = false;
      }
    },
    [userId] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const start = useCallback(async (sessionType: SessionType) => {
    await startSession(sessionType);
  }, [startSession]);

  const resume = useCallback(async (sessionType: SessionType, existingSessionId: string) => {
    if (!userId) {
      setError('You must be signed in to continue a conversation');
      setStatus('idle');
      return;
    }

    setError(null);
    try {
      const [existingSession, existingMessages] = await Promise.all([
        getSession(userId, existingSessionId),
        getMessages(userId, existingSessionId),
      ]);
      if (!existingSession) {
        throw new Error('Conversation not found');
      }
      if (existingSession.type !== sessionType) {
        throw new Error('Conversation type mismatch');
      }

      const seededTranscript: TranscriptEntry[] = existingMessages.map(message => ({
        role: message.role,
        text: message.content,
        timestamp: getTimestampMillis(message.timestamp),
      }));

      await startSession(sessionType, {
        continueSessionId: existingSessionId,
        seededTranscript,
      });
    } catch (err) {
      setError(toUserFacingErrorMessage(err, 'Failed to continue voice conversation'));
      setStatus('idle');
    }
  }, [userId, startSession]);

  const end = useCallback(async () => {
    if (!userId || !sessionIdRef.current) return;
    intentionalCloseRef.current = true;
    setStatus('ending');

    // Stop mic
    workletNodeRef.current?.disconnect();
    workletNodeRef.current = null;
    mediaStreamRef.current?.getTracks().forEach(t => t.stop());
    mediaStreamRef.current = null;

    // Flush any remaining buffered audio
    flushSendBuffer();
    if (turnNudgeTimerRef.current) clearTimeout(turnNudgeTimerRef.current);
    if (flushTimerRef.current) clearTimeout(flushTimerRef.current);

    // Close WebSocket
    wsRef.current?.close();
    wsRef.current = null;

    // Flush any lingering partial transcript entries
    flushUserPartialTranscript();
    flushAssistantPartialTranscript();

    const sessionId = sessionIdRef.current;
    const finalTranscript = transcriptRef.current;
    const persistFromIndex = persistedTranscriptOffsetRef.current;
    const newTranscriptEntries = finalTranscript.slice(persistFromIndex);

    // Clean up audio contexts immediately
    captureCtxRef.current?.close();
    playbackCtxRef.current?.close();
    captureCtxRef.current = null;
    playbackCtxRef.current = null;

    setStatus('completed');

    // Save transcript + run extraction in the background (don't block the user)
    (async () => {
      try {
        // Mark extracting immediately so Home can render the analyzing card on return.
        await updateSession(userId, sessionId, { status: 'extracting' });
        await reportVoiceUsageBestEffort(sessionId);
        await Promise.all(
          newTranscriptEntries.map((entry, i) =>
            addMessage(userId, sessionId, entry.role, entry.text, persistFromIndex + i)
          )
        );
        const summary = await callProcessExtraction(sessionId, {});
        setExtractionSummary(summary);
      } catch (err) {
        console.error('Background extraction failed:', err);
        try {
          await updateSession(userId, sessionId, { status: 'completed' });
        } catch { /* best-effort cleanup */ }
      }
    })();
  }, [userId, reportVoiceUsageBestEffort]); // eslint-disable-line react-hooks/exhaustive-deps

  const discard = useCallback(async () => {
    const activeSessionId = sessionIdRef.current;
    if (userId && activeSessionId) {
      await reportVoiceUsageBestEffort(activeSessionId);
    }
    suppressVoiceRuntime();
    setIsAssistantSpeaking(false);
    isAssistantSpeakingRef.current = false;

    if (!userId || !activeSessionId) {
      setStatus('idle');
      return;
    }

    setStatus('ending');
    setError(null);
    try {
      await abandonSession(userId, activeSessionId);
      setStatus('completed');
      setSessionId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to discard conversation');
      setStatus('active');
      throw err;
    }
  }, [reportVoiceUsageBestEffort, suppressVoiceRuntime, userId]);

  const sendText = useCallback((text: string) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || !text.trim()) return;

    appendTranscript('user', text.trim());
    logVoiceDebug('sendText called', {
      provider: activeProviderRef.current,
      length: text.trim().length,
    });

    if (activeProviderRef.current === 'openai' || activeProviderRef.current === 'elevenlabs' || activeProviderRef.current === 'grok') {
      ws.send(
        JSON.stringify({
          type: 'conversation.item.create',
          item: {
            type: 'message',
            role: 'user',
            content: [{ type: 'input_text', text: text.trim() }],
          },
        })
      );
      requestOpenAiResponse();
    } else if (activeProviderRef.current === 'gemini') {
      ws.send(
        JSON.stringify({
          clientContent: {
            turns: [{ role: 'user', parts: [{ text: text.trim() }] }],
            turnComplete: true,
          },
        })
      );
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleMute = useCallback(() => {
    setIsMuted(prev => {
      isMutedRef.current = !prev;
      return !prev;
    });
  }, []);

  const disconnect = useCallback(() => {
    suppressVoiceRuntime();
    setSessionId(null);
    setStatus('idle');
    setIsAssistantSpeaking(false);
    isAssistantSpeakingRef.current = false;
  }, [suppressVoiceRuntime]);

  const resetEndDetection = useCallback(() => {
    lastUserEndIntentAtRef.current = 0;
    lastCoachFarewellAtRef.current = 0;
    userEndIntentDetectedRef.current = false;
  }, []);

  return {
    sessionId,
    status,
    transcript,
    realtimeExtractions,
    extractionSummary,
    isMuted,
    isAssistantSpeaking,
    error,
    start,
    resume,
    end,
    discard,
    disconnect,
    sendText,
    toggleMute,
    resetEndDetection,
  };
}
